import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { MaskedLines } from "./Reveal";
import { useScrollTo } from "./SmoothScroll";

const ease = [0.16, 1, 0.3, 1];

export const Hero = () => {
  const { t, lang } = useLang();
  const go = useScrollTo();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 80, damping: 18 });
  const cx = useSpring(useMotionValue(-100), { stiffness: 300, damping: 30 });
  const cy = useSpring(useMotionValue(-100), { stiffness: 300, damping: 30 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
    cx.set(e.clientX - r.left);
    cy.set(e.clientY - r.top);
  };

  return (
    <section id="topo" ref={ref} onMouseMove={onMove} className="relative min-h-[100svh] overflow-hidden bg-[#0A0A0C]" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -top-10">
        <motion.img src="/img/car2.jpg" alt="" initial={{ scale: 1.25, opacity: 0 }} animate={{ scale: 1.05, opacity: 0.38 }}
          transition={{ duration: 2.4, ease }} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0C]/60 via-[#0A0A0C]/40 to-[#0A0A0C]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0C] via-[#0A0A0C]/50 to-transparent" />
      </motion.div>

      <motion.div style={{ x: cx, y: cy }} className="pointer-events-none absolute left-0 top-0 z-30 hidden md:block -ml-6 -mt-6 h-12 w-12 rounded-full border border-[#C5A059]/70 mix-blend-screen" />

      <div className="relative z-10 mx-auto max-w-[1440px] px-5 md:px-10 pt-32 md:pt-40 pb-16 grid lg:grid-cols-12 gap-10 lg:gap-6 items-end min-h-[100svh]">
        <motion.div style={{ y: textY }} className="lg:col-span-7 xl:col-span-7">
          <motion.p initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1, delay: 0.2 }}
            className="eyebrow mb-8 flex items-center gap-4"><span className="h-px w-12 bg-[#C5A059]" />{t.hero.over}</motion.p>
          <h1 className="font-display font-light text-[3.4rem] leading-[0.92] sm:text-7xl lg:text-[7.5rem] xl:text-[8.5rem] tracking-[-0.03em] text-[#F9F9F9]" data-testid="hero-title">
            <MaskedLines key={lang} lines={t.hero.lines} animateOnLoad delay={0.35} italicIndex={1} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1.1 }}
            className="mt-10 max-w-lg text-base md:text-lg text-zinc-300 leading-relaxed">{t.hero.sub}</motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1.3 }}
            className="mt-10 flex flex-wrap items-center gap-4">
            <button onClick={() => go("#reservar")} className="btn-gold group" data-testid="hero-book-btn">
              {t.hero.cta}<ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button onClick={() => go("#tours")} className="btn-ghost" data-testid="hero-tours-btn">{t.hero.cta2}</button>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative flex justify-center lg:justify-end [perspective:1200px]">
          <motion.div style={{ rotateX: rx, rotateY: ry }} initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.6, delay: 0.5, ease }}
            className="relative w-[78%] sm:w-[60%] lg:w-full max-w-[440px] aspect-[3/4] rounded-t-full overflow-hidden border border-[#C5A059]/40 shadow-[0_40px_120px_-30px_rgba(197,160,89,0.35)] [transform-style:preserve-3d]"
            data-testid="hero-arch">
            <img src="/img/lisbon2.jpg" alt="Lisboa, Rio Tejo" className="h-full w-full object-cover scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C]/80 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-0 right-0 text-center [transform:translateZ(60px)]">
              <p className="font-display italic text-2xl text-[#F3E5AB]">Lisboa</p>
              <p className="text-[11px] uppercase tracking-[0.35em] text-zinc-200 mt-1">38.7223° N · 9.1393° W</p>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 1.5 }}
            className="absolute -left-2 lg:-left-10 bottom-10 h-28 w-28 md:h-32 md:w-32">
            <svg viewBox="0 0 100 100" className="h-full w-full animate-spin-slow">
              <defs><path id="circ" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
              <text className="fill-[#C5A059] text-[9.5px] uppercase tracking-[0.3em]"><textPath href="#circ">{t.hero.badge} · </textPath></text>
            </svg>
            <span className="absolute inset-0 m-auto h-3 w-3 rotate-45 bg-[#E5C158]" />
          </motion.div>
        </div>
      </div>

      <motion.button onClick={() => go("#tours")} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-[11px] uppercase tracking-[0.35em] text-zinc-400" data-testid="hero-scroll-indicator">
        {t.hero.scroll}<ArrowDown size={14} className="animate-bounce text-[#C5A059]" />
      </motion.button>
    </section>
  );
};
